import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const QUESTIONS = [
  {
    q: "Se ti fermassi per una settimana, la tua attività…",
    options: [
      { label: "Continuerebbe senza problemi", pts: 0 },
      { label: "Rallenterebbe parecchio", pts: 1 },
      { label: "Si fermerebbe del tutto", pts: 2 },
    ],
  },
  {
    q: "Quante ore al giorno passi su compiti che qualcun altro potrebbe fare?",
    options: [
      { label: "Meno di 1 ora", pts: 0 },
      { label: "Tra 1 e 3 ore", pts: 1 },
      { label: "Più di 3 ore", pts: 2 },
    ],
  },
  {
    q: "Hai procedure scritte da consegnare a un collaboratore?",
    options: [
      { label: "Sì, complete e aggiornate", pts: 0 },
      { label: "Solo alcune, sparse", pts: 1 },
      { label: "No, è tutto nella mia testa", pts: 2 },
    ],
  },
  {
    q: "Quanto spesso le urgenze interrompono il tuo lavoro importante?",
    options: [
      { label: "Raramente", pts: 0 },
      { label: "Qualche volta a settimana", pts: 1 },
      { label: "Ogni giorno", pts: 2 },
    ],
  },
  {
    q: "Quanto tempo dedichi a te stessa ogni settimana?",
    options: [
      { label: "Più di 5 ore", pts: 0 },
      { label: "Tra 2 e 5 ore", pts: 1 },
      { label: "Meno di 2 ore", pts: 2 },
    ],
  },
];

const resultFor = (score) => {
  if (score <= 3)
    return {
      title: "Buone fondamenta",
      text: "Hai già iniziato a rendere la tua attività indipendente. Il percorso ti aiuta a sistematizzare ciò che funziona e a liberare altre ore ogni settimana.",
    };
  if (score <= 7)
    return {
      title: "La tua attività dipende troppo da te",
      text: "Buona parte del lavoro passa ancora dalle tue mani. Con le procedure giuste puoi delegare senza perdere il controllo, una cosa alla volta.",
    };
  return {
    title: "Sei il collo di bottiglia della tua azienda",
    text: "Ogni decisione e ogni urgenza passano da te. Il costo non è solo il tempo: è la tua energia. Il percorso di 7 giorni è pensato esattamente per questo.",
  };
};

export const Quiz = () => {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const done = step >= QUESTIONS.length;
  const result = resultFor(score);
  const hours = Math.round(2 + score * 1.3);

  const answer = (pts) => {
    setScore((s) => s + pts);
    setStep((s) => s + 1);
  };

  const reset = () => {
    setStep(0);
    setScore(0);
  };

  return (
    <section id="quiz" data-testid="quiz-section" className="py-24 sm:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Quiz iniziale · 5 domande</span>
          <h2 className="mt-4 font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Quanto la tua attività <em className="italic text-terracotta">dipende da te?</em>
          </h2>
          <p className="mt-4 text-base text-taupe">Due minuti di sincerità. Il risultato potrebbe sorprenderti.</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 overflow-hidden rounded-[2rem] border border-ink/10 bg-white shadow-xl shadow-espresso/5">
            <div className="h-1.5 w-full bg-sand">
              <motion.div
                className="h-full bg-terracotta"
                animate={{ width: `${(Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="p-8 sm:p-12">
              <AnimatePresence mode="wait">
                {!done ? (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone2">
                      Domanda {step + 1} di {QUESTIONS.length}
                    </p>
                    <h3 className="mt-4 font-serif text-xl text-ink sm:text-2xl">{QUESTIONS[step].q}</h3>
                    <div className="mt-8 space-y-3">
                      {QUESTIONS[step].options.map((opt, i) => (
                        <button
                          key={i}
                          data-testid={`quiz-option-${step}-${i}`}
                          onClick={() => answer(opt.pts)}
                          className="group flex w-full items-center justify-between rounded-2xl border border-ink/10 bg-linen px-6 py-4 text-left text-sm font-medium text-ink transition-all hover:border-terracotta hover:bg-terracotta/5 sm:text-base"
                        >
                          {opt.label}
                          <ArrowRight className="h-4 w-4 text-stone2 transition-all group-hover:translate-x-1 group-hover:text-terracotta" />
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    data-testid="quiz-result"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                  >
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone2">Il tuo risultato</p>
                    <h3 className="mt-4 font-serif text-2xl text-terracotta sm:text-3xl">{result.title}</h3>
                    <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-taupe">{result.text}</p>
                    <div className="mx-auto mt-8 inline-block rounded-2xl bg-sand px-8 py-5">
                      <p className="font-serif text-3xl text-ink">fino a {hours} ore</p>
                      <p className="mt-1 text-xs font-medium uppercase tracking-widest text-taupe">recuperabili ogni settimana</p>
                    </div>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                      <button
                        onClick={() => scrollToId("acquista")}
                        data-testid="quiz-result-cta"
                        className="rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-linen shadow-lg shadow-terracotta/25 transition-all hover:-translate-y-0.5 hover:bg-terracotta-dark"
                      >
                        Scopri il percorso · €29
                      </button>
                      <button
                        onClick={reset}
                        data-testid="quiz-restart-button"
                        className="flex items-center gap-2 text-sm font-medium text-stone2 transition-colors hover:text-terracotta"
                      >
                        <RotateCcw className="h-4 w-4" /> Rifai il quiz
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
